export interface OnboardingSubmittedStateProps {
  /** ISO timestamp do submit. Formatado em pt-BR para exibição. */
  submittedAt: string | null;
  /** Nome do template (ex: "Diagnóstico KM Gestão v1"). */
  templateName: string;
}

/**
 * Estado "já respondido" da rota `/onboarding/:token`. Aparece quando
 * o user abre o link depois de ter submetido (reload, compartilhou
 * consigo mesmo, voltou para conferir). Mostra confirmação visual +
 * próximo passo explícito (consultora entrará em contato).
 *
 * Não renderiza as respostas porque (a) elas contêm dados sensíveis
 * que podem ter sido editados no form; (b) a rota é pública — se
 * alguém com o token vazar visualiza, viola LGPD. Confirmação sem
 * expor dados é o padrão.
 */
export function OnboardingSubmittedState({
  submittedAt,
  templateName,
}: OnboardingSubmittedStateProps) {
  const formatted = submittedAt
    ? new Date(submittedAt).toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div
      role="status"
      className="rounded-lg border border-border bg-card p-6 text-center shadow-sm sm:p-8"
    >
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        {/* Checkmark simples — evita dependência de lucide aqui */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="size-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 12l5 5L20 6" />
        </svg>
      </div>
      <h1 className="text-xl font-semibold text-foreground sm:text-2xl">
        Obrigado pelas suas respostas!
      </h1>
      <p className="mt-2 text-sm text-muted-foreground sm:text-base">
        Recebemos o seu preenchimento do <strong>{templateName}</strong>.
        {formatted ? <> Registrado em {formatted}.</> : null}
      </p>
      <div className="mt-6 space-y-3 rounded-md bg-muted/50 p-4 text-left sm:p-5">
        <p className="text-sm font-medium text-foreground sm:text-base">
          Próximo passo: sua proposta comercial
        </p>
        <p className="text-sm text-muted-foreground sm:text-base">
          A consultora já está analisando suas respostas para preparar uma{' '}
          <strong>proposta comercial personalizada</strong> para sua clínica. Em breve você receberá
          o contato com os detalhes e valores.
        </p>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Pode fechar esta página com tranquilidade.
      </p>
    </div>
  );
}
